/**
 * Shared highlight color palette for Lightning readers.
 *
 * The five canonical keys are stored verbatim in `user_annotations.color`,
 * so notebook themes (and any future color→theme mapping) can key off them
 * without translation. Keep keys stable; tune only the swatch/background
 * values.
 */

export type HighlightPaletteColor = 'yellow' | 'blue' | 'green' | 'pink' | 'purple';

export interface HighlightPaletteEntry {
  key: HighlightPaletteColor;
  label: string;
  /** Solid dot shown in pickers. */
  swatch: string;
  /** Translucent text background on light reader themes. */
  bgLight: string;
  /** Translucent text background on dark reader themes. */
  bgDark: string;
}

export const HIGHLIGHT_PALETTE: readonly HighlightPaletteEntry[] = [
  { key: 'yellow', label: 'Yellow', swatch: '#E8C547', bgLight: 'rgba(232, 197, 71, 0.35)', bgDark: 'rgba(232, 197, 71, 0.30)' },
  { key: 'blue',   label: 'Blue',   swatch: '#5D8AA8', bgLight: 'rgba(93, 138, 168, 0.28)', bgDark: 'rgba(93, 138, 168, 0.35)' },
  { key: 'green',  label: 'Green',  swatch: '#7BA05B', bgLight: 'rgba(123, 160, 91, 0.28)', bgDark: 'rgba(123, 160, 91, 0.32)' },
  { key: 'pink',   label: 'Pink',   swatch: '#D98BA3', bgLight: 'rgba(217, 139, 163, 0.30)', bgDark: 'rgba(217, 139, 163, 0.32)' },
  { key: 'purple', label: 'Purple', swatch: '#9A7BB8', bgLight: 'rgba(154, 123, 184, 0.28)', bgDark: 'rgba(154, 123, 184, 0.34)' },
] as const;

const BY_KEY: Record<string, HighlightPaletteEntry> = Object.fromEntries(
  HIGHLIGHT_PALETTE.map((entry) => [entry.key, entry]),
);

/**
 * Resolve any stored color value to a palette entry. Unknown and legacy
 * values (e.g. rows saved as 'orange' before purple replaced it) render
 * as yellow rather than losing the highlight.
 */
export function resolveHighlightColor(color: string | null | undefined): HighlightPaletteEntry {
  return BY_KEY[color ?? ''] ?? BY_KEY.yellow;
}
